import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Syringe, ShieldCheck } from 'lucide-react';
import './Login.css';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    async function handleSubmit(e) {
        e.preventDefault();

        try {
            setError('');
            setLoading(true);
            await login(email, password);
            navigate('/dashboard');
        } catch (err) {
            setError('Failed to sign in. Please check your credentials.');
            console.error(err);
        }

        setLoading(false);
    }

    return (
        <div className="login-container">
            {/* Brand panel */}
            <div className="login-brand-panel">
                <div className="login-brand-content">
                    <div className="login-brand-mark">
                        <Syringe size={20} />
                    </div>
                    <h1 className="login-brand-title">Vaccine Tracker</h1>
                    <p className="login-brand-tagline">
                        Accurate dose records. Every patient, every visit, on schedule.
                    </p>

                    <div className="login-pulse-wrap" aria-hidden="true">
                        <svg viewBox="0 0 400 80" className="login-pulse-line" preserveAspectRatio="none">
                            <polyline
                                points="0,40 60,40 80,40 95,10 110,70 125,40 145,40 170,40 185,20 200,60 215,40 240,40 400,40"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>

                    <div className="login-brand-footnote">
                        <ShieldCheck size={15} />
                        <span>Records encrypted &amp; access-controlled</span>
                    </div>
                </div>
            </div>

            {/* Form panel */}
            <div className="login-form-panel">
                <div className="login-card">
                    <div className="login-header">
                        <span className="login-eyebrow">Staff Sign In</span>
                        <h2 className="login-title">Welcome back</h2>
                        <p className="login-subtitle">Sign in to access the vaccination dashboard</p>
                    </div>

                    {error && <div className="error-message" role="alert">{error}</div>}

                    <form onSubmit={handleSubmit} className="login-form">
                        <div className="form-group">
                            <label htmlFor="login-email" className="form-label">Email Address</label>
                            <div className="input-wrapper">
                                <Mail size={16} className="input-icon" />
                                <input
                                    id="login-email"
                                    type="email"
                                    className="form-input has-icon"
                                    placeholder="name@hospital.com"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    aria-invalid={error ? "true" : "false"}
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <div className="form-label-row">
                                <label htmlFor="login-password" className="form-label">Password</label>
                                <a href="#reset" className="forgot-link">Forgot password?</a>
                            </div>
                            <div className="input-wrapper">
                                <Lock size={16} className="input-icon" />
                                <input
                                    id="login-password"
                                    type="password"
                                    className="form-input has-icon"
                                    placeholder="••••••••"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    aria-invalid={error ? "true" : "false"}
                                />
                            </div>
                        </div>

                        <button disabled={loading} type="submit" className="login-button">
                            {loading ? 'Signing In…' : 'Sign In'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}